import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ShoppingBag, Minus, Plus, Trash2, Search, MessageCircle, X, AlertTriangle, ThumbsDown } from "lucide-react";
import ProductImage from "@/components/shared/ProductImage";
import { MOQ, QTY_STEP, CATEGORIES, currencySymbol as curSym } from "@/lib/constants";

const curSymbol = curSym;

const BRANDS = ["All", "ASOS", "Mango", "Meski", "New Look", "Next", "Zara", "River Island", "Topshop"];
const TAGS = [
    { key: "", label: "All" },
    { key: "new_arrival", label: "New Arrivals" },
    { key: "best_seller", label: "Best Sellers" },
];

export default function Shop() {
    const [products, setProducts] = useState([]);
    const [brand, setBrand] = useState("All");
    const [tag, setTag] = useState("");
    const [category, setCategory] = useState("");
    const [q, setQ] = useState("");
    const [selected, setSelected] = useState(null);
    const [selectedQty, setSelectedQty] = useState(MOQ);
    const [showCart, setShowCart] = useState(false);
    const { items, add, remove, setQty, clear, total, count, belowMoq } = useCart();
    const { user } = useAuth();
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const [notes, setNotes] = useState("");
    const [placedOrder, setPlacedOrder] = useState(null);
    const [summary, setSummary] = useState({ ready: 0, pending: 0 });
    const [rejecting, setRejecting] = useState("");

    const reject = async (id) => {
        if (!window.confirm("Mark this design as Not Interested? It will disappear from your shop.")) return;
        setRejecting(id);
        try {
            await api.post(`/products/${id}/reject`);
            setProducts((rows) => rows.filter((p) => p.id !== id));
            toast.success("Marked as Not Interested");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not update");
        } finally {
            setRejecting("");
        }
    };

    const load = async () => {
        const params = {};
        if (brand !== "All") params.brand = brand;
        if (tag) params.tag = tag;
        if (category) params.category = category;
        const r = await api.get("/products", { params });
        setProducts(r.data);
        try {
            const s = await api.get("/products/summary");
            setSummary(s.data);
        } catch (e) { /* silent */ }
    };

    useEffect(() => { load(); }, [brand, tag, category]);

    const filtered = useMemo(() => {
        if (!q) return products;
        return products.filter((p) => p.title.toLowerCase().includes(q.toLowerCase()));
    }, [products, q]);

    const doCheckout = async () => {
        if (items.length === 0) return;
        if (belowMoq) {
            toast.error(`Minimum ${MOQ} pieces per item — please adjust quantities`);
            return;
        }
        try {
            const r = await api.post("/orders", {
                items: items.map((i) => ({
                    product_id: i.product_id,
                    title: i.title,
                    brand: i.brand,
                    price: i.price,
                    currency: i.currency,
                    quantity: i.quantity,
                    image_url: i.image_url,
                })),
                notes,
            });
            setPlacedOrder(r.data);
            clear();
            setCheckoutOpen(false);
            setNotes("");
            setShowCart(false);
            toast.success("Order placed successfully");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Order failed");
        }
    };

    const shareWhatsApp = (order) => {
        const lines = [
            `*New Order · ${order.order_number}*`,
            `Client: ${user?.company || user?.name} (ID ${user?.login_id})`,
            "",
            ...order.items.map((it) => `• ${it.title}${it.sku_code ? ` (${it.sku_code})` : ""} × ${it.quantity} pcs @ ${curSymbol(it.currency)}${it.price.toFixed(2)}/pc = ${curSymbol(it.currency)}${(it.price * it.quantity).toFixed(2)}`),
            "",
            `*Total pieces: ${order.items.reduce((s, i) => s + i.quantity, 0)}*`,
            `*Total value: ${curSymbol(order.currency)}${order.total.toFixed(2)}*`,
        ];
        if (order.notes) lines.push("", `Notes: ${order.notes}`);
        const text = encodeURIComponent(lines.join("\n"));
        window.open(`https://wa.me/?text=${text}`, "_blank");
    };

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-6 sm:py-10">
            {/* Personalised hero */}
            <div className="mb-10">
                <div className="flex items-center gap-3 mb-4">
                    <div className="h-2 w-16" style={{ background: user?.brand_color || "#0A0A0A" }}/>
                    <div className="overline" style={{ color: user?.brand_color || "#0A0A0A" }}>Curated for {user?.brand_display_name || user?.company || user?.name}</div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-end">
                    <div className="lg:col-span-2">
                        <h1 className="font-display text-3xl sm:text-5xl md:text-6xl tracking-tight leading-[0.95]">
                            New this drop.<br/><span className="italic font-normal" style={{ color: user?.brand_color || "#0A0A0A" }}>{user?.brand_display_name || "Your"}</span> selection.
                        </h1>
                        <p className="mt-5 font-body text-sm text-neutral-500 max-w-lg leading-relaxed">
                            Every piece here has been pushed to <b style={{ color: user?.brand_color }}>{user?.brand_display_name || user?.company}</b> by the A V Corp buying team. MOQ {MOQ} pieces per style. Add to cart, checkout, and share the summary on WhatsApp.
                        </p>
                    </div>
                    <div className="border border-border p-5 bg-white">
                        <div className="overline text-neutral-500">This Catalog</div>
                        <div className="grid grid-cols-3 gap-4 mt-3">
                            <div>
                                <div className="font-display text-3xl tabular-nums leading-none" data-testid="stat-styles">{products.length}</div>
                                <div className="overline text-neutral-500 mt-1">Styles</div>
                            </div>
                            <div>
                                <div className="font-display text-3xl tabular-nums leading-none" style={{ color: user?.brand_color }} data-testid="stat-new">{products.filter(p => (p.tags||[]).includes("new_arrival")).length}</div>
                                <div className="overline text-neutral-500 mt-1">New</div>
                            </div>
                            <div>
                                <div className="font-display text-3xl tabular-nums leading-none" data-testid="stat-best">{products.filter(p => (p.tags||[]).includes("best_seller")).length}</div>
                                <div className="overline text-neutral-500 mt-1">Best</div>
                            </div>
                        </div>
                        <div className="border-t border-border mt-4 pt-4 flex items-center gap-2">
                            <div className="h-2 w-2 rounded-full" style={{ background: user?.brand_color }}/>
                            <div className="overline text-neutral-500">{user?.brand_accent || "House"} theme</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filter bar — brand chips hidden for legal/white-label reasons; every product carries the A V Corp SKU */}
            {summary.pending > 0 && (
                <div className="mb-4 border border-neutral-300 bg-neutral-50 px-4 py-2.5 flex flex-wrap items-center gap-3 text-xs" data-testid="shop-syncing-banner">
                    <span className="inline-block w-2 h-2 animate-pulse rounded-full" style={{ background: user?.brand_color || "#0A0A0A" }}></span>
                    <span className="font-body text-neutral-700 flex-1 min-w-[180px]">
                        Showing <span className="font-semibold">{summary.ready} ready styles</span> · <span className="font-semibold">{summary.pending} more</span> are being prepared and will appear here shortly.
                    </span>
                </div>
            )}
            <div className="border-t border-b border-border py-3 sm:py-4 mb-6 sm:mb-8 sticky top-14 md:top-16 bg-white/95 backdrop-blur z-30">
                <div className="flex flex-wrap items-center gap-3">
                    {TAGS.map((t) => (
                        <button
                            key={t.key || "all"}
                            onClick={() => setTag(t.key)}
                            data-testid={`tag-chip-${t.key || "all"}`}
                            className={`px-3 py-1 border uppercase tracking-widest text-[10px] font-semibold ${tag === t.key ? "bg-yellow-400 border-black text-black" : "border-neutral-300 text-neutral-600 hover:border-black hover:text-black"}`}
                        >
                            {t.label}
                        </button>
                    ))}
                    <div className="relative ml-auto max-w-xs w-full">
                        <Search className="h-4 w-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5}/>
                        <Input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search products…"
                            className="pl-9 rounded-none h-9 border-black"
                            data-testid="shop-search"
                        />
                    </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span className="overline text-neutral-500 mr-1">Category</span>
                    <button
                        onClick={() => setCategory("")}
                        data-testid="cat-chip-all"
                        className={`px-3 py-1 border uppercase tracking-widest text-[10px] font-semibold ${!category ? "bg-black text-white border-black" : "border-neutral-300 text-neutral-600 hover:border-black hover:text-black"}`}
                    >All</button>
                    {CATEGORIES.map((c) => (
                        <button
                            key={c}
                            onClick={() => setCategory(c)}
                            data-testid={`cat-chip-${c.replace(/\s+/g,'-').toLowerCase()}`}
                            className={`px-3 py-1 border uppercase tracking-widest text-[10px] font-semibold ${category === c ? "bg-black text-white border-black" : "border-neutral-300 text-neutral-600 hover:border-black hover:text-black"}`}
                        >{c}</button>
                    ))}
                </div>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
                {filtered.map((p, idx) => (
                    <div key={p.id} className="brutal-card group cursor-pointer stagger-item" style={{ animationDelay: `${idx * 25}ms` }} onClick={() => setSelected(p)} data-testid={`shop-product-${p.id}`}>
                        <div className="aspect-[3/4] bg-neutral-50 border-b border-border overflow-hidden relative">
                            <ProductImage product={p} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"/>
                            {(p.visible_to || []).length > 0 && (
                                <div className="absolute top-3 right-3 uppercase tracking-widest text-[8px] px-1.5 py-0.5 text-white" style={{ background: user?.brand_color || "#0A0A0A" }}>
                                    For You
                                </div>
                            )}
                            <div className="absolute top-3 left-3 flex flex-col gap-1">
                                {(p.tags || []).includes("new_arrival") && (
                                    <Badge variant="outline" className="rounded-none uppercase tracking-widest text-[9px] bg-white border-black">New</Badge>
                                )}
                                {(p.tags || []).includes("best_seller") && (
                                    <Badge variant="outline" className="rounded-none uppercase tracking-widest text-[9px] bg-yellow-400 border-black">Best Seller</Badge>
                                )}
                            </div>
                        </div>                        <div className="p-4">
                            <div className="flex items-center justify-between">
                                <div className="overline text-neutral-500" data-testid={`shop-sku-${p.id}`}>{p.sku_code || "A V Corp"}</div>
                                {p.category && <div className="overline text-neutral-400 text-[9px]">{p.category}</div>}
                            </div>
                            <div className="font-body text-sm mt-1 line-clamp-2 min-h-[2.5em]">{p.title}</div>
                            <div className="flex items-end justify-between mt-3">
                                <div>
                                    <div className="font-display text-xl tabular-nums leading-none">{curSymbol(p.currency)}{p.price?.toFixed(2)}<span className="text-[10px] uppercase tracking-widest text-neutral-500 ml-1">/pc</span></div>
                                    <div className="text-[9px] uppercase tracking-widest text-neutral-500 mt-1">MOQ {MOQ} pcs</div>
                                </div>
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={(e) => { e.stopPropagation(); reject(p.id); }}
                                        disabled={rejecting === p.id}
                                        className="text-[10px] uppercase tracking-widest border border-red-700 text-red-700 px-2 py-1 hover:bg-red-700 hover:text-white transition-colors disabled:opacity-40 flex items-center gap-1"
                                        data-testid={`reject-${p.id}`}
                                        title="Not interested"
                                    >
                                        <ThumbsDown className="h-3 w-3" strokeWidth={1.5}/>
                                        {rejecting === p.id ? "…" : "Pass"}
                                    </button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); add(p); toast.success(`Added ${MOQ} pcs to cart`); }}
                                        className="text-[10px] uppercase tracking-widest border border-black px-2 py-1 hover:bg-black hover:text-white transition-colors"
                                        data-testid={`add-to-cart-${p.id}`}
                                    >Add</button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            {filtered.length === 0 && (
                <div className="border border-dashed border-border p-16 text-center">
                    <div className="overline text-neutral-500">Empty</div>
                    <div className="font-display text-2xl mt-2">No products match your filters</div>
                </div>
            )}

            {/* Product detail dialog */}
            <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setSelectedQty(MOQ); } }}>
                <DialogContent className="rounded-none max-w-4xl p-0 border-black">
                    {selected && (
                        <div className="grid grid-cols-1 md:grid-cols-2">
                            <div className="aspect-[3/4] md:aspect-auto bg-neutral-50">
                                <ProductImage product={selected} className="w-full h-full object-cover"/>
                            </div>
                            <div className="p-8 flex flex-col">
                                <div className="overline text-neutral-500 font-mono" data-testid="shop-detail-sku">{selected.sku_code || "A V Corp"}</div>
                                <h2 className="font-display text-3xl tracking-tight leading-tight mt-2">{selected.title}</h2>
                                <div className="mt-3 flex gap-2">
                                    {(selected.tags || []).includes("new_arrival") && <Badge variant="outline" className="rounded-none uppercase tracking-widest text-[9px] bg-white border-black">New Arrival</Badge>}
                                    {(selected.tags || []).includes("best_seller") && <Badge variant="outline" className="rounded-none uppercase tracking-widest text-[9px] bg-yellow-400 border-black">Best Seller</Badge>}
                                </div>
                                <div className="mt-6 flex items-baseline gap-3">
                                    <div className="font-display text-4xl tabular-nums">{curSymbol(selected.currency)}{selected.price?.toFixed(2)}</div>
                                    <div className="overline text-neutral-500">per piece</div>
                                </div>
                                <div className="mt-2 overline text-neutral-500">Minimum Order · {MOQ} pieces</div>
                                <p className="text-sm font-body text-neutral-600 mt-4 leading-relaxed line-clamp-6">{selected.description || "—"}</p>

                                <div className="mt-6 border-t border-border pt-4">
                                    <div className="overline text-neutral-600 mb-2">Quantity (pieces)</div>
                                    <div className="flex items-center gap-3">
                                        <div className="flex items-center border border-black">
                                            <button
                                                onClick={() => setSelectedQty(Math.max(MOQ, selectedQty - QTY_STEP))}
                                                className="w-10 h-10 hover:bg-black hover:text-white"
                                                data-testid="detail-qty-minus"
                                            ><Minus className="h-3.5 w-3.5 mx-auto" strokeWidth={1.5}/></button>
                                            <input
                                                type="number"
                                                value={selectedQty}
                                                onChange={(e) => setSelectedQty(Math.max(0, parseInt(e.target.value) || 0))}
                                                onBlur={(e) => { if (selectedQty < MOQ) setSelectedQty(MOQ); }}
                                                min={MOQ}
                                                step={QTY_STEP}
                                                className="w-24 h-10 text-center font-mono text-sm border-x border-black bg-white focus:outline-none tabular-nums"
                                                data-testid="detail-qty-input"
                                            />
                                            <button
                                                onClick={() => setSelectedQty(selectedQty + QTY_STEP)}
                                                className="w-10 h-10 hover:bg-black hover:text-white"
                                                data-testid="detail-qty-plus"
                                            ><Plus className="h-3.5 w-3.5 mx-auto" strokeWidth={1.5}/></button>
                                        </div>
                                        <div className="font-body text-sm text-neutral-500">
                                            = <span className="font-display text-lg text-black">{curSymbol(selected.currency)}{(selected.price * selectedQty).toFixed(2)}</span>
                                        </div>
                                    </div>
                                    {selectedQty < MOQ && (
                                        <div className="text-[11px] text-red-700 mt-2 flex items-center gap-1 font-body">
                                            <AlertTriangle className="h-3 w-3" strokeWidth={1.5}/> Minimum {MOQ} pcs required
                                        </div>
                                    )}
                                </div>

                                <div className="flex-1"/>
                                <div className="mt-6">
                                    <Button
                                        onClick={() => { add(selected, selectedQty); toast.success(`Added ${selectedQty} pcs to cart`); setSelected(null); setSelectedQty(MOQ); }}
                                        disabled={selectedQty < MOQ}
                                        className="w-full h-12 rounded-none bg-black text-white hover:bg-neutral-800 uppercase tracking-widest text-xs font-semibold disabled:opacity-40"
                                        data-testid="dialog-add-to-cart"
                                    >
                                        <ShoppingBag className="h-4 w-4 mr-2" strokeWidth={1.5}/> Add {selectedQty} pcs to Order
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Floating cart button */}
            {count > 0 && (
                <button
                    onClick={() => setShowCart(true)}
                    className="fixed bottom-6 right-6 z-40 h-14 pl-5 pr-6 text-white uppercase tracking-widest text-xs font-semibold hover:opacity-90 inline-flex items-center gap-3 shadow-lg"
                    style={{ background: user?.brand_color || "#0A0A0A" }}
                    data-testid="open-cart-btn"
                >
                    <ShoppingBag className="h-4 w-4" strokeWidth={1.5}/> {count} pcs · £{total.toFixed(2)}
                </button>
            )}

            {/* Cart Sheet */}
            <Sheet open={showCart} onOpenChange={setShowCart}>
                <SheetContent side="right" className="rounded-none w-full sm:max-w-md p-0 border-l border-black">
                    <div className="flex flex-col h-full">
                        <div className="p-6 border-b border-border flex items-center justify-between">
                            <div>
                                <div className="overline text-neutral-500">Your Order</div>
                                <div className="font-display text-2xl tracking-tight leading-none mt-1">Cart ({count})</div>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            {items.length === 0 && <div className="p-8 text-sm text-neutral-500 font-body text-center">Your cart is empty</div>}
                            {items.map((i) => (
                                <div key={i.product_id} className="p-4 border-b border-border flex gap-3" data-testid={`cart-item-${i.product_id}`}>
                                    <div className="w-16 h-20 bg-neutral-50 shrink-0">
                                        <ProductImage product={i} className="w-full h-full object-cover"/>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="overline text-neutral-500">{i.brand}</div>
                                        <div className="font-body text-sm line-clamp-2">{i.title}</div>
                                        <div className="text-[11px] text-neutral-500 font-body mt-1 tabular-nums">{curSymbol(i.currency)}{i.price.toFixed(2)}/pc</div>
                                        <div className="flex items-center justify-between mt-2">
                                            <div className="flex items-center border border-black">
                                                <button onClick={() => setQty(i.product_id, Math.max(0, i.quantity - QTY_STEP))} className="w-7 h-7 hover:bg-black hover:text-white" data-testid={`cart-minus-${i.product_id}`}><Minus className="h-3 w-3 mx-auto" strokeWidth={1.5}/></button>
                                                <input
                                                    type="number"
                                                    value={i.quantity}
                                                    onChange={(e) => setQty(i.product_id, Math.max(0, parseInt(e.target.value) || 0))}
                                                    min={0}
                                                    step={QTY_STEP}
                                                    className="w-14 h-7 text-center font-mono text-xs border-x border-black bg-white focus:outline-none tabular-nums"
                                                    data-testid={`cart-qty-input-${i.product_id}`}
                                                />
                                                <button onClick={() => setQty(i.product_id, i.quantity + QTY_STEP)} className="w-7 h-7 hover:bg-black hover:text-white" data-testid={`cart-plus-${i.product_id}`}><Plus className="h-3 w-3 mx-auto" strokeWidth={1.5}/></button>
                                            </div>
                                            <div className="font-display text-sm tabular-nums">{curSymbol(i.currency)}{(i.price * i.quantity).toFixed(2)}</div>
                                        </div>
                                        {i.quantity < MOQ && (
                                            <div className="text-[10px] text-red-700 mt-1.5 flex items-center gap-1 font-body">
                                                <AlertTriangle className="h-3 w-3" strokeWidth={1.5}/> Below MOQ ({MOQ} pcs)
                                            </div>
                                        )}
                                    </div>
                                    <button onClick={() => remove(i.product_id)} className="text-neutral-400 hover:text-red-700 self-start" data-testid={`remove-from-cart-${i.product_id}`}>
                                        <X className="h-4 w-4" strokeWidth={1.5}/>
                                    </button>
                                </div>
                            ))}
                        </div>
                        {items.length > 0 && (
                            <div className="p-6 border-t border-border bg-neutral-50">
                                <div className="flex items-center justify-between mb-1">
                                    <div className="overline">Pieces</div>
                                    <div className="font-mono text-sm tabular-nums">{count.toLocaleString()}</div>
                                </div>
                                <div className="flex items-center justify-between mb-4">
                                    <div className="overline">Total</div>
                                    <div className="font-display text-3xl tabular-nums">£{total.toFixed(2)}</div>
                                </div>
                                {belowMoq && (
                                    <div className="mb-3 border border-red-400 bg-red-50 text-red-800 p-2 text-[11px] font-body flex items-start gap-2">
                                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" strokeWidth={1.5}/>
                                        <span>Some items are below the {MOQ}-piece minimum. Adjust before checkout.</span>
                                    </div>
                                )}
                                <Button
                                    onClick={() => setCheckoutOpen(true)}
                                    disabled={belowMoq}
                                    className="w-full h-12 rounded-none bg-black text-white hover:bg-neutral-800 uppercase tracking-widest text-xs font-semibold disabled:opacity-40"
                                    data-testid="checkout-btn"
                                >
                                    Checkout
                                </Button>
                            </div>
                        )}
                    </div>
                </SheetContent>
            </Sheet>

            {/* Checkout dialog */}
            <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
                <DialogContent className="rounded-none max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl tracking-tight">Confirm Order</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="flex justify-between text-sm font-body">
                            <span className="text-neutral-500">Line items</span>
                            <span>{items.length}</span>
                        </div>
                        <div className="flex justify-between text-sm font-body">
                            <span className="text-neutral-500">Total pieces</span>
                            <span className="font-mono">{count.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-sm font-body">
                            <span className="text-neutral-500">Total</span>
                            <span className="font-display text-2xl tabular-nums">£{total.toFixed(2)}</span>
                        </div>
                        <div>
                            <label className="overline text-neutral-600">Notes (optional)</label>
                            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-none border-black mt-2" placeholder="Size breakdown, delivery instructions, sample requests…" data-testid="checkout-notes"/>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCheckoutOpen(false)} className="rounded-none border-black">Cancel</Button>
                        <Button onClick={doCheckout} className="rounded-none bg-black text-white hover:bg-neutral-800" data-testid="confirm-order-btn">Place Order</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Success + WhatsApp share */}
            <Dialog open={!!placedOrder} onOpenChange={(o) => !o && setPlacedOrder(null)}>
                <DialogContent className="rounded-none max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-display text-3xl tracking-tight">Order Placed</DialogTitle>
                    </DialogHeader>
                    {placedOrder && (
                        <div className="space-y-4">
                            <div className="font-mono text-xs text-neutral-500">{placedOrder.order_number}</div>
                            <div className="border border-border p-4">
                                <div className="overline text-neutral-500">Total value</div>
                                <div className="font-display text-4xl tabular-nums mt-1">£{placedOrder.total.toFixed(2)}</div>
                                <div className="text-xs font-body text-neutral-500 mt-2">
                                    {placedOrder.items.length} styles · {placedOrder.items.reduce((s, i) => s + i.quantity, 0).toLocaleString()} pieces · Status: {placedOrder.status}
                                </div>
                            </div>
                            <Button
                                onClick={() => shareWhatsApp(placedOrder)}
                                className="w-full h-12 rounded-none bg-[#25D366] text-white hover:bg-[#128C7E] uppercase tracking-widest text-xs font-semibold"
                                data-testid="share-whatsapp-btn"
                            >
                                <MessageCircle className="h-4 w-4 mr-2" strokeWidth={1.5}/> Share on WhatsApp
                            </Button>
                            <div className="text-xs text-neutral-500 font-body text-center leading-relaxed">
                                Your order is saved and visible to the manufacturer. Use WhatsApp to send a quick confirmation.
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
